import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMount } from 'react-use';

import {
  AudioDeviceOption,
  MICROPHONE_DEVICES_CHANGED_EVENT,
  getMicrophoneDevices,
} from '@eluve/mic-utils';
import { useAnalytics } from '@eluve/posthog-react';

export interface UseMicrophoneDeviceSelectorParams {
  getDevices?: () => Promise<AudioDeviceOption[]>;
  onSelectedDeviceChange: (device: AudioDeviceOption | null) => void;
  selectedId: string | null;
  preferredMicrophoneNames?: string[];
}

export interface UseMicrophoneDeviceSelectorResult {
  options: AudioDeviceOption[];
  selectedMicrophone: AudioDeviceOption | undefined;
  handleValueChange: (deviceId: string) => void;
  handleOpenChange: (open: boolean) => void;
}

export const useMicrophoneDeviceSelector = ({
  getDevices = getMicrophoneDevices,
  onSelectedDeviceChange,
  selectedId,
  preferredMicrophoneNames,
}: UseMicrophoneDeviceSelectorParams): UseMicrophoneDeviceSelectorResult => {
  const analytics = useAnalytics('microphone-device-selector');

  const isLoading = useRef(false);
  const [options, setOptions] = useState<AudioDeviceOption[]>([]);

  const selectedMicrophone = useMemo(
    () => options.find((option) => option.deviceId === selectedId),
    [options, selectedId],
  );

  const fetchOptions = useCallback(async () => {
    if (isLoading.current) {
      return;
    }
    isLoading.current = true;
    const devices = await getDevices();
    setOptions(devices);
    isLoading.current = false;
  }, [getDevices]);

  useMount(() => fetchOptions());

  useEffect(() => {
    if (navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', fetchOptions);
    }

    window.addEventListener(MICROPHONE_DEVICES_CHANGED_EVENT, fetchOptions);

    return () => {
      if (navigator.mediaDevices) {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          fetchOptions,
        );
      }
      window.removeEventListener(
        MICROPHONE_DEVICES_CHANGED_EVENT,
        fetchOptions,
      );
    };
  }, [fetchOptions]);

  useEffect(() => {
    if (isLoading.current) {
      return;
    }

    const canSelectDefault = !selectedId && options.length > 0;
    const shouldClearOldId = selectedId && !selectedMicrophone;

    if (canSelectDefault || shouldClearOldId) {
      let selectedDevice = null;

      // 1. Try to find preferred device by name (if configured)
      // Loop through preferred names in order and find first match
      if (preferredMicrophoneNames && preferredMicrophoneNames.length > 0) {
        for (const preferredName of preferredMicrophoneNames) {
          selectedDevice = options.find((device) =>
            device.label.toLowerCase().includes(preferredName.toLowerCase()),
          );
          if (selectedDevice) {
            break;
          }
        }
      }

      // 2. Fall back to "default" device
      selectedDevice ??= options.find(
        (device) => device.deviceId === 'default',
      );

      // 3. Fall back to first available
      selectedDevice ??= options[0];

      onSelectedDeviceChange(selectedDevice ?? null);
    }
  }, [
    onSelectedDeviceChange,
    options,
    selectedId,
    selectedMicrophone,
    preferredMicrophoneNames,
  ]);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      analytics.capture('appointment.audio_source_dropdown_clicked', {
        audio_source_count: options.length,
      });
    }
  };

  const handleValueChange = (deviceId: string) => {
    const device = options.find((option) => option.deviceId === deviceId);
    analytics.capture('appointment.audio_source_selected', {
      audio_source: device?.label ?? deviceId,
    });
    onSelectedDeviceChange(device ?? null);
  };

  return {
    options,
    selectedMicrophone,
    handleValueChange,
    handleOpenChange,
  };
};
